import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Lock } from 'lucide-react';
import type {
  EmergencyArrangementRow, EmergencyBillPage, EmergencyBillRow, EmergencyBillStatus,
  EmergencyClinicalAccess,
} from '@dru/types';
import { EMERGENCY_BILL_STATUS_LABEL, EMERGENCY_SUMMARY_SOURCE_LABEL, formatPaise } from '@dru/types';
import {
  Alert, Button, DataTable, Drawer, EmptyState, Field, FilterBar, SearchInput, StatusBadge,
  Textarea, TextSkeleton, type Tone, useToast,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useAuth, errorMessage } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { usePages } from '../../../lib/usePages';
import { formatDate, formatDateTime } from '../../../lib/format';
import { Dialog } from '../../../components/Dialog';
import { PageHeader } from '../../../layouts/ConsoleShell';

/**
 * Emergency treatment bills from partner hospitals and labs (Requirement 2).
 *
 * Every reader sees the employee, the visit date, the venue, why it was
 * billed, the invoice and the amount. The presenting issue and treatment
 * appear only as far as the company's health-data policy and the reader's
 * own role allow — opening the invoice never opens the medical record.
 * A submitted bill is awaiting review: nothing here approves or pays it.
 */

const STATUSES: EmergencyBillStatus[] = ['SUBMITTED', 'QUERIED', 'REVIEWED'];
const TONE: Record<EmergencyBillStatus, Tone> = { SUBMITTED: 'pending', QUERIED: 'warning', REVIEWED: 'success' };
const ACCESS_LINE: Record<EmergencyClinicalAccess, string> = {
  FULL: 'Your company shares the full clinical summary with your role.',
  SUMMARY: 'Your company shares the presenting issue and treatment only.',
  NONE: 'Clinical details are withheld under your company’s health-data policy or your role.',
};

export function EmergencyBills() {
  const { permissions } = useAuth();
  const canRead = permissions.has('emergency.read');
  const [params, setParams] = useSearchParams();
  const status = (STATUSES as string[]).includes(params.get('status') ?? '') ? params.get('status') as EmergencyBillStatus : '';
  const applied = params.get('q') ?? '';
  const [search, setSearch] = useState(applied);
  const [openId, setOpenId] = useState<string | null>(null);
  const [venues, setVenues] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== applied) setParams((p) => { const n = new URLSearchParams(p); if (search.trim()) n.set('q', search.trim()); else n.delete('q'); return n; }, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [search, applied, setParams]);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('billStatus', status);
  if (applied) qs.set('q', applied);
  const key = qs.toString();
  const [access, setAccess] = useState<EmergencyClinicalAccess | null>(null);
  const page = usePages<EmergencyBillRow>(
    async (cursor) => {
      if (!canRead) return { items: [], nextCursor: null };
      const p = await api.get<EmergencyBillPage>(`/emergency-bills?${key}${cursor ? `&cursor=${cursor}` : ''}`);
      setAccess(p.clinicalAccess);
      return p;
    },
    [key, canRead],
  );

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Emergency bills" />
        <EmptyState title="Emergency bills are not part of your role" hint="Ask your company administrator for access." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Emergency bills"
        hint="Emergency treatment your employees received at partner hospitals and labs, with the venue's invoice. A submitted bill is awaiting your review — it is not approved or paid."
        actions={<Button variant="secondary" onClick={() => setVenues(true)}>Arranged venues</Button>}
      />
      {access && (
        <p className="flex items-center gap-2 text-small text-neutral-600">
          {access === 'NONE' && <Lock className="h-4 w-4" aria-hidden />}{ACCESS_LINE[access]}
        </p>
      )}
      <FilterBar
        filters={[{ key: 'status', label: 'Status', allLabel: 'Any status',
          options: STATUSES.map((s) => ({ value: s, label: EMERGENCY_BILL_STATUS_LABEL[s] })) }]}
        value={{ status: status || undefined }}
        onChange={(v) => setParams((p) => { const n = new URLSearchParams(p); if (v.status) n.set('status', v.status); else n.delete('status'); return n; }, { replace: true })}
      >
        <SearchInput value={search} onValueChange={setSearch} aria-label="Search by employee or invoice number"
                     placeholder="Employee name, ID or invoice no." className="w-full sm:w-72" />
      </FilterBar>
      <DataTable<EmergencyBillRow>
        rows={page.items}
        loading={page.loading}
        error={page.error}
        onRetry={page.reload}
        hasMore={page.hasMore}
        onLoadMore={page.loadMore}
        loadingMore={page.loadingMore}
        rowKey={(b) => b.id}
        onRowClick={(b) => setOpenId(b.id)}
        empty={{
          title: status || applied ? 'No bill matches these filters' : 'No emergency bills yet',
          hint: 'Bills appear here once Dr Ultimate confirms an emergency visit sent by an arranged hospital or lab.',
        }}
        columns={[
          { key: 'employee', header: 'Employee', cell: (b) => (
            <div><div className="font-medium">{b.employee.fullName}</div>
              <div className="text-caption text-neutral-600">{[b.employee.employeeCode, b.employee.plant].filter(Boolean).join(' · ')}</div></div>
          ) },
          { key: 'date', header: 'Visit date', cell: (b) => formatDate(b.visitDate) },
          { key: 'venue', header: 'Hospital', cell: (b) => b.partner.name, hideOnMobile: true },
          { key: 'reason', header: 'Reason for billing', cell: (b) => b.billingReason ?? '—', hideOnMobile: true },
          { key: 'invoice', header: 'Invoice', cell: (b) => b.invoiceNumber, hideOnMobile: true },
          { key: 'amount', header: 'Billed', align: 'right', cell: (b) => formatPaise(b.billedAmountPaise) },
          { key: 'status', header: 'Status', cell: (b) => <StatusBadge tone={TONE[b.billStatus]}>{EMERGENCY_BILL_STATUS_LABEL[b.billStatus]}</StatusBadge> },
        ]}
      />
      {openId && <BillDrawer id={openId} onClose={() => setOpenId(null)} onChanged={page.reload} />}
      {venues && <ArrangedVenuesDialog onClose={() => setVenues(false)} />}
    </div>
  );
}

function BillDrawer({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { permissions } = useAuth();
  const canReview = permissions.has('emergency.review');
  const { toast } = useToast();
  const q = useQuery(() => api.get<EmergencyBillRow & { clinicalAccess: EmergencyClinicalAccess }>(`/emergency-bills/${id}`), [id]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const b = q.data;

  const review = async (outcome: 'REVIEWED' | 'QUERIED') => {
    setBusy(true); setError(null);
    try {
      await api.post(`/emergency-bills/${id}/review`, { outcome, ...(note.trim() ? { note: note.trim() } : {}) });
      toast('success', outcome === 'REVIEWED' ? 'Marked reviewed' : 'Query recorded');
      q.reload(); onChanged();
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const openInvoice = async () => {
    try { const g = await api.get<{ url: string }>(`/emergency-bills/${id}/invoice`); window.open(`${g.url}?inline=1`, '_blank', 'noopener'); }
    catch (e) { setError(errorMessage(e)); }
  };

  return (
    <Drawer title="Emergency bill" onClose={onClose} width="lg">
      {q.error ? <Alert>{q.error}</Alert> : !b ? <TextSkeleton lines={8} /> : (
        <div className="space-y-5">
          <div>
            <div className="text-h4 text-neutral-900">{b.employee.fullName}</div>
            <div className="text-small text-neutral-600">{[b.employee.employeeCode, b.employee.plant].filter(Boolean).join(' · ')}</div>
          </div>
          <dl className="grid grid-cols-2 gap-3 text-small max-md:grid-cols-1">
            <div><dt className="text-neutral-600">Hospital</dt><dd className="text-neutral-900">{b.partner.name}</dd></div>
            <div><dt className="text-neutral-600">Visit date</dt><dd className="text-neutral-900">{formatDate(b.visitDate)}</dd></div>
            <div><dt className="text-neutral-600">Reason for billing</dt><dd className="text-neutral-900">{b.billingReason ?? '—'}</dd></div>
            <div><dt className="text-neutral-600">Invoice number</dt><dd className="text-neutral-900">{b.invoiceNumber}</dd></div>
            <div><dt className="text-neutral-600">Billed amount</dt><dd className="font-semibold text-neutral-900">{formatPaise(b.billedAmountPaise)}</dd></div>
            <div><dt className="text-neutral-600">Submitted</dt><dd className="text-neutral-900">{formatDateTime(b.submittedAt)}</dd></div>
          </dl>
          {b.hasInvoice && (
            <Button variant="secondary" icon={<FileText className="h-4 w-4" />} onClick={openInvoice} className="max-md:w-full">Open invoice</Button>
          )}

          <section className="space-y-2">
            <h3 className="text-small font-semibold text-neutral-900">Clinical summary</h3>
            {b.clinical ? (
              <div className="space-y-2 rounded-card border border-neutral-200 p-3 text-small">
                <div><div className="text-caption text-neutral-600">Presenting issue</div><p className="whitespace-pre-wrap">{b.clinical.presentingIssue ?? '—'}</p></div>
                {b.clinical.examinationFindings !== undefined && (
                  <div><div className="text-caption text-neutral-600">Examination findings</div><p className="whitespace-pre-wrap">{b.clinical.examinationFindings ?? '—'}</p></div>
                )}
                <div><div className="text-caption text-neutral-600">Treatment given</div><p className="whitespace-pre-wrap">{b.clinical.treatmentGiven ?? '—'}</p></div>
                <p className="text-caption text-neutral-600">
                  Confirmed by {b.clinical.confirmedByName ?? 'Dr Ultimate'}
                  {b.clinical.summarySource && ` · ${EMERGENCY_SUMMARY_SOURCE_LABEL[b.clinical.summarySource]}`}
                  {b.clinical.aiModel && ` (${b.clinical.aiModel})`}
                </p>
                {b.clinical.rawNotes !== undefined && (
                  <details className="text-caption">
                    <summary className="cursor-pointer text-neutral-700 max-md:min-h-10">Doctor's original notes</summary>
                    <p className="mt-1 whitespace-pre-wrap rounded-input bg-neutral-50 p-2 text-small text-neutral-900">{b.clinical.rawNotes}</p>
                  </details>
                )}
              </div>
            ) : (
              <p className="flex items-center gap-2 text-small text-neutral-600"><Lock className="h-4 w-4" aria-hidden />{ACCESS_LINE.NONE}</p>
            )}
          </section>

          <section className="space-y-2">
            <h3 className="text-small font-semibold text-neutral-900">Review</h3>
            <div className="flex items-center gap-2">
              <StatusBadge tone={TONE[b.billStatus]}>{EMERGENCY_BILL_STATUS_LABEL[b.billStatus]}</StatusBadge>
              {b.reviewedAt && <span className="text-caption text-neutral-600">{formatDateTime(b.reviewedAt)}</span>}
            </div>
            {b.reviewNote && <p className="text-small text-neutral-700">“{b.reviewNote}”</p>}
            {error && <Alert>{error}</Alert>}
            {canReview && b.billStatus !== 'REVIEWED' && (
              <>
                <Field label="Note" hint="Required for a query: say what needs explaining.">
                  {(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
                </Field>
                <div className="flex flex-wrap gap-2">
                  <Button loading={busy} onClick={() => review('REVIEWED')} className="max-md:w-full">Mark reviewed</Button>
                  <Button variant="secondary" disabled={busy || note.trim().length < 3} onClick={() => review('QUERIED')} className="max-md:w-full">Query the bill</Button>
                </div>
                <p className="text-caption text-neutral-600">Reviewing records that you have checked the bill. It does not approve or pay it.</p>
              </>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}

/** The venues Dr Ultimate arranged for the company's employees. Read-only here. */
function ArrangedVenuesDialog({ onClose }: { onClose: () => void }) {
  const rows = useQuery(() => api.get<{ items: EmergencyArrangementRow[] }>('/emergency-bills/arrangements'), []);
  return (
    <Dialog title="Arranged venues" info="Hospitals and labs where your employees may be treated in an emergency. Dr Ultimate arranges these; ask your Dr Ultimate contact to add or remove one." onClose={onClose}>
      {rows.error ? <Alert>{rows.error}</Alert> : rows.loading ? <TextSkeleton lines={3} /> : (rows.data?.items ?? []).length === 0 ? (
        <p className="text-small text-neutral-600">No venue arranged yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-200 rounded-card border border-neutral-200">
          {rows.data!.items.map((r) => (
            <li key={r.id} className="px-3 py-2">
              <div className="font-medium text-neutral-900">{r.partner.name}</div>
              <div className="text-caption text-neutral-600">{[r.partner.code, r.partner.city, r.note].filter(Boolean).join(' · ')}</div>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
